import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";

import {
    ModeOfSaleRow,
    setCustomerInformationModeSaleCode,
    storeKitchenTicketType,
    setCustomerInformationModeSaleName,
    setCustomerInformationModeSaleNumTel,
    setCustomerInformationModeSaleAdress,
    setCustomerInformationModeSaleCity,
    setCustomerInformationModeSaleCompany,
} from "@components/Common/PrinterSetting/stores";

import { PrinterRowData, PrinterAppId } from "../../types";
import { SalesModeApiItem, buildModeRows } from "../../hooks/salesModesHelper";

import ModeSwitchList from "./ModeSwitchList";

import "../index.css";

type PrintCustomerInformationType = {
    rowData: PrinterRowData;
    salesModesData: SalesModeApiItem[];
    appId: PrinterAppId;
};

const TESTID_BASE = "printer-setting-kitchen-info";

export default function PrintCustomerInformation({
    rowData,
    salesModesData,
    appId,
}: PrintCustomerInformationType) {
    const { t } = useTranslation();
    const {
        customerInformationModeSaleName,
        customerInformationModeSaleNumTel,
        customerInformationModeSaleAdress,
        customerInformationModeSaleCity,
        customerInformationModeSaleCode,
        customerInformationModeSaleCompany,
    } = useSnapshot(storeKitchenTicketType);

    const [salesModesNames, setSalesModesNames] = React.useState<
        ModeOfSaleRow[]
    >([]);
    const [salesModesNumTel, setSalesModesNumTel] = React.useState<
        ModeOfSaleRow[]
    >([]);
    const [salesModesCode, setSalesModesCode] = React.useState<ModeOfSaleRow[]>(
        []
    );
    const [salesModesCompany, setSalesModesCompany] = React.useState<
        ModeOfSaleRow[]
    >([]);
    const [salesModesAdress, setSalesModesAdress] = React.useState<
        ModeOfSaleRow[]
    >([]);
    const [salesModesCity, setSalesModesCity] = React.useState<ModeOfSaleRow[]>(
        []
    );

    const fetchModeOfSale = React.useCallback(() => {
        const kitchenTicket =
            typeof rowData["Kitchen_ticket"] === "object"
                ? rowData["Kitchen_ticket"]
                : undefined;
        const info = kitchenTicket?.customer_information_mode_sale;

        setSalesModesNames(
            buildModeRows(
                salesModesData,
                info?.customerInformationModeSaleName,
                appId
            )
        );
        setSalesModesNumTel(
            buildModeRows(
                salesModesData,
                info?.customerInformationModeSaleNumTel,
                appId
            )
        );
        setSalesModesAdress(
            buildModeRows(
                salesModesData,
                info?.customerInformationModeSaleAdress,
                appId
            )
        );
        setSalesModesCity(
            buildModeRows(
                salesModesData,
                info?.customerInformationModeSaleCity,
                appId
            )
        );
        setSalesModesCompany(
            buildModeRows(
                salesModesData,
                info?.customerInformationModeSaleCompany,
                appId
            )
        );
        setSalesModesCode(
            buildModeRows(
                salesModesData,
                info?.customerInformationModeSaleCode,
                appId
            )
        );
    }, [salesModesData, rowData, appId]);

    useEffect(() => {
        fetchModeOfSale();
    }, [fetchModeOfSale]);

    useEffect(() => {
        setCustomerInformationModeSaleAdress(salesModesAdress);
        setCustomerInformationModeSaleCompany(salesModesCompany);
        setCustomerInformationModeSaleCode(salesModesCode);
        setCustomerInformationModeSaleCity(salesModesCity);
        setCustomerInformationModeSaleNumTel(salesModesNumTel);
        setCustomerInformationModeSaleName(salesModesNames);
    }, [
        salesModesAdress,
        salesModesCompany,
        salesModesCode,
        salesModesNames,
        salesModesCity,
        salesModesNumTel,
    ]);

    const makeToggle = (
        rows: readonly ModeOfSaleRow[],
        setRows: (next: ModeOfSaleRow[]) => void
    ) => (item: ModeOfSaleRow, index: number): void => {
        const newVisibility: ModeOfSaleRow[] = JSON.parse(
            JSON.stringify(rows)
        );
        newVisibility[index].state = !item.state;
        setRows(newVisibility);
    };

    return (
        <div>
            <div className="d-flex  align-items-center d-flex flex-wrap p-2">
                <div>
                    <b>{t("Print customer information by mode of sale")}</b>
                </div>
            </div>
            <ModeSwitchList
                label="Name"
                testIdPrefix={`${TESTID_BASE}-name-mode`}
                rows={customerInformationModeSaleName}
                onToggle={makeToggle(
                    customerInformationModeSaleName,
                    setCustomerInformationModeSaleName
                )}
            />
            <ModeSwitchList
                label="Phone number"
                testIdPrefix={`${TESTID_BASE}-numtel-mode`}
                rows={customerInformationModeSaleNumTel}
                onToggle={makeToggle(
                    customerInformationModeSaleNumTel,
                    setCustomerInformationModeSaleNumTel
                )}
            />
            <ModeSwitchList
                label="Company"
                testIdPrefix={`${TESTID_BASE}-city-mode`}
                rows={customerInformationModeSaleCity}
                onToggle={makeToggle(
                    customerInformationModeSaleCity,
                    setCustomerInformationModeSaleCity
                )}
            />
            <ModeSwitchList
                label="Address"
                testIdPrefix={`${TESTID_BASE}-address-mode`}
                rows={customerInformationModeSaleAdress}
                onToggle={makeToggle(
                    customerInformationModeSaleAdress,
                    setCustomerInformationModeSaleAdress
                )}
            />
            <ModeSwitchList
                label="Postal code"
                testIdPrefix={`${TESTID_BASE}-code-mode`}
                rows={customerInformationModeSaleCode}
                onToggle={makeToggle(
                    customerInformationModeSaleCode,
                    setCustomerInformationModeSaleCode
                )}
            />
            <ModeSwitchList
                label="City"
                testIdPrefix={`${TESTID_BASE}-company-mode`}
                rows={customerInformationModeSaleCompany}
                onToggle={makeToggle(
                    customerInformationModeSaleCompany,
                    setCustomerInformationModeSaleCompany
                )}
            />
        </div>
    );
}
